import type { ContractPeriod, DegreeDays, DjuSettings, EnergyKey, LngLat, PeriodKey, PlanBuilding, RatioSet, RatioTable, UsageKey, WeatherStation } from './types';
import { distanceKm } from './geo';

// ─── Référentiels (VALEURS DE DÉMONSTRATION — à fournir par le métier) ─────────

export const USAGES: { key: UsageKey; label: string }[] = [
  { key: 'resCollectif', label: 'Résidentiel collectif' },
  { key: 'resIndividuel', label: 'Résidentiel individuel' },
  { key: 'tertiaire', label: 'Tertiaire' },
  { key: 'enseignement', label: 'Enseignement' },
  { key: 'sante', label: 'Santé' },
  { key: 'autre', label: 'Autre' },
];

export const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'avant1948', label: '< 1948' },
  { key: 'p1948_1974', label: '1948-1974' },
  { key: 'p1975_1999', label: '1975-1999' },
  { key: 'p2000_2012', label: '2000-2012' },
  { key: 'apres2012', label: '> 2012' },
];

export const USAGE_LABEL = Object.fromEntries(USAGES.map((u) => [u.key, u.label])) as Record<UsageKey, string>;
export const PERIOD_LABEL = Object.fromEntries(PERIODS.map((p) => [p.key, p.label])) as Record<PeriodKey, string>;

/** Période de construction de référence : porte le ratio saisi et le coefficient 100 %. */
export const REF_PERIOD: PeriodKey = 'avant1948';

/** Postes d'un ratio : chauffage, eau chaude sanitaire, froid. */
export const ENERGIES: { key: EnergyKey; label: string; short: string; icon: string }[] = [
  { key: 'chauffage', label: 'Chauffage', short: 'Chauf.', icon: 'local_fire_department' },
  { key: 'ecs', label: 'Eau chaude sanitaire', short: 'ECS', icon: 'water_drop' },
  { key: 'froid', label: 'Froid', short: 'Froid', icon: 'ac_unit' },
];

/** DJ de référence des ratios de base : les ratios par défaut y sont ramenés par station. */
export const REF_DJU = 2500;
export const REF_DJCLIM = 110;

/** Besoins de chauffage de référence (kWh/m²/an) — valeurs de démonstration. */
const BASE_CHAUFFAGE: Record<UsageKey, Record<PeriodKey, number>> = {
  resCollectif: { avant1948: 165, p1948_1974: 148, p1975_1999: 119, p2000_2012: 89, apres2012: 48 },
  resIndividuel: { avant1948: 195, p1948_1974: 176, p1975_1999: 142, p2000_2012: 103, apres2012: 53 },
  tertiaire: { avant1948: 190, p1948_1974: 173, p1975_1999: 146, p2000_2012: 114, apres2012: 70 },
  enseignement: { avant1948: 155, p1948_1974: 140, p1975_1999: 118, p2000_2012: 93, apres2012: 56 },
  sante: { avant1948: 215, p1948_1974: 196, p1975_1999: 169, p2000_2012: 137, apres2012: 90 },
  autre: { avant1948: 138, p1948_1974: 124, p1975_1999: 104, p2000_2012: 83, apres2012: 50 },
};

/** ECS : peu dépendante de la période de construction. */
const BASE_ECS: Record<UsageKey, number> = {
  resCollectif: 25, resIndividuel: 25, tertiaire: 10, enseignement: 8, sante: 45, autre: 8,
};

/** Froid : croissant avec la période de construction (taux d'équipement). */
const BASE_FROID: Record<UsageKey, number> = {
  resCollectif: 4, resIndividuel: 2, tertiaire: 20, enseignement: 9, sante: 28, autre: 6,
};
const FROID_FACTOR: Record<PeriodKey, number> = {
  avant1948: 0.6, p1948_1974: 0.7, p1975_1999: 1, p2000_2012: 1.3, apres2012: 1.6,
};

const buildRatios = (heat: number, cool: number): RatioTable =>
  Object.fromEntries(USAGES.map((u) => [
    u.key,
    Object.fromEntries(PERIODS.map((p) => [
      p.key,
      {
        chauffage: Math.round(BASE_CHAUFFAGE[u.key][p.key] * heat),
        ecs: BASE_ECS[u.key],
        froid: Math.round(BASE_FROID[u.key] * FROID_FACTOR[p.key] * cool),
      } satisfies RatioSet,
    ])),
  ])) as RatioTable;

/**
 * Ratios de référence (station aux DJ de référence) — valeurs de démonstration.
 * Le chauffage décroît d'une période à la suivante : les coefficients de performance
 * dérivés sont donc tous < 100 % et décroissants de gauche à droite.
 */
export const DEFAULT_RATIOS: RatioTable = buildRatios(1, 1);

/**
 * Ratios par défaut pour un couple de degrés-jours : chauffage au prorata des DJU,
 * froid au prorata des DJClim. FORMULE DE DÉMONSTRATION, à confirmer par le métier.
 */
export const ratiosForDegrees = (dju: number, djclim: number): RatioTable =>
  buildRatios(dju / REF_DJU, djclim / REF_DJCLIM);

export const ratiosForStation = (s: WeatherStation): RatioTable => ratiosForDegrees(s.dju, s.djclim);

export const ratioTotal = (r: RatioSet): number => r.chauffage + r.ecs + r.froid;

/** Ratio synthétique d'une typologie : période de construction de référence, poste par poste. */
export function usageRatio(t: RatioTable, u: UsageKey): RatioSet {
  return t[u][REF_PERIOD];
}

/** Fixe le ratio de la période de référence : les autres périodes suivent leur coefficient. */
export function setUsageRatio(t: RatioTable, u: UsageKey, e: EnergyKey, target: number): RatioTable {
  const cur = t[u][REF_PERIOD][e];
  const periods = Object.fromEntries(PERIODS.map((p) => {
    const v = cur > 0 ? (t[u][p.key][e] * target) / cur : target;
    return [p.key, { ...t[u][p.key], [e]: Math.round(v * 10) / 10 }];
  })) as RatioTable[UsageKey];
  return { ...t, [u]: periods };
}

/**
 * Calibre des ratios saisis pour un couple de degrés-jours sur un autre couple :
 * chauffage au prorata des DJU, froid au prorata des DJClim, ECS inchangée.
 * FORMULE DE DÉMONSTRATION, à confirmer par le métier.
 */
export function calibrateRatios(t: RatioTable, from: DegreeDays, to: DegreeDays): RatioTable {
  const kHeat = from.dju > 0 ? to.dju / from.dju : 1;
  const kCool = from.djclim > 0 ? to.djclim / from.djclim : 1;
  const r1 = (v: number) => Math.round(v * 10) / 10;
  return Object.fromEntries(USAGES.map((u) => [
    u.key,
    Object.fromEntries(PERIODS.map((p) => {
      const s = t[u.key][p.key];
      return [p.key, { chauffage: r1(s.chauffage * kHeat), ecs: s.ecs, froid: r1(s.froid * kCool) } satisfies RatioSet];
    })),
  ])) as RatioTable;
}

/** Alimenté par les stations du jeu de démonstration préparé avant ouverture des parcours. */
export const STATIONS: WeatherStation[] = [];

/** Station la plus proche d'un point : proposition par défaut du projet. */
export function nearestStation(point: LngLat | null, stations: WeatherStation[] = STATIONS): WeatherStation {
  if (!stations.length) throw new Error('Aucune station source disponible');
  if (!point) return stations[0];
  return stations.reduce((best, s) => (distanceKm(point, s.position) < distanceKm(point, best.position) ? s : best), stations[0]);
}

export const DEFAULT_DJU: DjuSettings = { stationId: '', reference: REF_DJU };

export const RATIO_MIN = 0;
export const RATIO_MAX = 1000;
export const isValidRatio = (v: number) => Number.isFinite(v) && v > RATIO_MIN && v <= RATIO_MAX;
export const isValidDju = (v: number) => Number.isFinite(v) && v >= 500 && v <= 5000;
export const isValidDjclim = (v: number) => Number.isFinite(v) && v >= 10 && v <= 1000;

export const cloneRatios = (r: RatioTable): RatioTable =>
  Object.fromEntries(USAGES.map((u) => [
    u.key,
    Object.fromEntries(PERIODS.map((p) => [p.key, { ...r[u.key][p.key] }])),
  ])) as RatioTable;

/** Nombre de valeurs (usage × période × poste) qui s'écartent de la référence. */
export function countModifiedRatios(r: RatioTable, ref: RatioTable = DEFAULT_RATIOS): number {
  let n = 0;
  for (const u of USAGES) {
    for (const p of PERIODS) {
      for (const e of ENERGIES) if (r[u.key][p.key][e.key] !== ref[u.key][p.key][e.key]) n += 1;
    }
  }
  return n;
}

export function periodOf(year: number): PeriodKey {
  if (year < 1948) return 'avant1948';
  if (year <= 1974) return 'p1948_1974';
  if (year <= 1999) return 'p1975_1999';
  if (year <= 2012) return 'p2000_2012';
  return 'apres2012';
}

// ─── Calcul de consommation ───────────────────────────────────────────────

export function stationOf(dju: DjuSettings): WeatherStation {
  const station = STATIONS.find((s) => s.id === dju.stationId);
  if (!station) throw new Error(`Station source inconnue : ${dju.stationId}`);
  return station;
}

/**
 * Les ratios projet sont déjà calibrés par poste sur les DJU/DJClim retenus.
 * Aucun second facteur climatique ne doit être appliqué au total (ECS comprise).
 */
export function djuCoefficient(dju: DjuSettings): number {
  stationOf(dju);
  return 1;
}

export interface BuildingConsumption {
  ratio: number | null;
  /** MWh/an */
  raw: number | null;
  coefficient: number;
  /** MWh/an */
  corrected: number | null;
}

export const canCalculateBuilding = (b: PlanBuilding): boolean =>
  b.usage !== null && b.period !== null && b.heatedArea !== null;

export function buildingConsumption(b: PlanBuilding, ratios: RatioTable, dju: DjuSettings): BuildingConsumption {
  const coefficient = djuCoefficient(dju);
  if (b.usage === null || b.period === null || b.heatedArea === null) {
    return { ratio: null, raw: null, coefficient, corrected: null };
  }
  const ratio = ratioTotal(ratios[b.usage][b.period]);
  const raw = (ratio * b.heatedArea) / 1000;
  return { ratio, raw, coefficient, corrected: raw * coefficient };
}

/** Consommation globale corrigée du périmètre, en MWh/an. */
export function totalConsumption(buildings: PlanBuilding[], ratios: RatioTable, dju: DjuSettings): number | null {
  let total = 0;
  for (const building of buildings) {
    const value = buildingConsumption(building, ratios, dju).corrected;
    if (value === null) return null;
    total += value;
  }
  return total;
}

/** Classes de la légende carte (MWh/an corrigés) — couleurs de jeu de données (hex autorisé). */
export const CONSUMPTION_CLASSES = [
  { max: 25, label: '< 25', color: '#fde6c8' },
  { max: 75, label: '25 – 75', color: '#f9bb7d' },
  { max: 200, label: '75 – 200', color: '#ef6b26' },
  { max: 500, label: '200 – 500', color: '#c2410c' },
  { max: Infinity, label: '≥ 500', color: '#7c2d12' },
] as const;

// ─── Géométrie ─────────────────────────────────────────────────────────────

export { boundsOf, centerOf, circlePolygon, closeRing, distanceKm, inPerimeter, pointInPolygon } from './geo';

// ─── Période de contrat ────────────────────────────────────────────────────

const MONTH_NAMES = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** `2027-04` → `avril 2027`. */
export function monthLabel(value: string): string {
  const [y, m] = value.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return '—';
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

const monthIndex = (value: string): number => {
  if (!/^\d{4,}-\d{2}$/.test(value)) return NaN;
  const [y, m] = value.split('-').map(Number);
  return Number.isFinite(y) && y > 0 && m >= 1 && m <= 12 ? y * 12 + (m - 1) : NaN;
};

/** Durée du contrat en mois, bornes incluses. 0 si la période est invalide. */
export function contractMonths(c: ContractPeriod): number {
  const a = monthIndex(c.start);
  const b = monthIndex(c.end);
  return Number.isFinite(a) && Number.isFinite(b) && b >= a ? b - a + 1 : 0;
}

export const isValidContract = (c: ContractPeriod): boolean => contractMonths(c) > 0;

/** `avril 2027 → mars 2047 · 20 ans`. Les durées non entières restent en mois. */
export function contractLabel(c: ContractPeriod): string {
  const months = contractMonths(c);
  if (months === 0) return 'Période à définir';
  const years = months / 12;
  const duration = Number.isInteger(years) ? `${years} an${years > 1 ? 's' : ''}` : `${months} mois`;
  return `${monthLabel(c.start)} → ${monthLabel(c.end)} · ${duration}`;
}

/** Période proposée par défaut : mois courant jusqu'au même mois dans 20 ans. */
export function defaultContract(from = new Date()): ContractPeriod {
  const start = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(start.getFullYear() + 20, start.getMonth(), 1);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return { start: iso(start), end: iso(end) };
}

// ─── Formats ───────────────────────────────────────────────────────────────

export const fmt = (n: number | null, digits = 0) =>
  n === null || !Number.isFinite(n) ? 'Non disponible' : n.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits });
export const gwh = (mwh: number | null) => mwh === null ? 'Non disponible' : `${fmt(mwh / 1000, 1)} GWh/an`;
export const today = () => new Date().toLocaleDateString('fr-FR');
